import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop component that handles both page navigation and hash navigation
 * This component should be used inside the Router but outside Routes
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Always scroll to top immediately when pathname changes
    // This ensures page navigations (like to /inventory) always reset scroll position
    if (!hash) {
      // For full page navigations without hash, immediately jump to top
      // Using 0,0 coordinates and no smooth behavior for instant reset
      window.scrollTo(0, 0);
      return;
    } 
    
    // Handle hash navigation (section scrolling)
    const scrollToSection = () => {
      const element = document.querySelector(hash);
      if (element) {
        // Get viewport width to apply different offsets for mobile/desktop
        const viewportWidth = window.innerWidth;
        
        // Smaller offset for mobile, larger for desktop
        const headerOffset = viewportWidth < 768 ? 60 : 80;
        
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        // Use smooth scrolling for better user experience
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        
        // For mobile, automatically close any open mobile menu
        // This targets common mobile menu implementations
        const mobileMenuButtons = document.querySelectorAll('[aria-expanded="true"], .mobile-menu-open');
        mobileMenuButtons.forEach(button => {
          if (button instanceof HTMLElement) {
            button.click();
          }
        });
      }
    };
    
    // Slight delay to ensure DOM is ready, especially on mobile
    setTimeout(scrollToSection, 100);
    
  }, [pathname, hash]);

  // Also handle manual clicks on anchor links
  useEffect(() => {
    const handleSectionLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      
      if (link && link.hash && link.pathname === window.location.pathname) {
        e.preventDefault();
        
        // Get the target element from the hash
        const element = document.querySelector(link.hash);
        if (element) {
          // Get viewport width to apply different offsets for mobile/desktop
          const viewportWidth = window.innerWidth;
          
          // Smaller offset for mobile, larger for desktop
          const headerOffset = viewportWidth < 768 ? 60 : 80;
          
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          // Use smooth scrolling for better user experience
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
          
          // Update the URL hash for deep linking
          window.history.pushState(null, '', link.hash);
        }
      }
    };
    
    document.addEventListener('click', handleSectionLinkClick);
    
    return () => {
      document.removeEventListener('click', handleSectionLinkClick);
    };
  }, []);

  return null;
};

export default ScrollToTop; 